/**
 * The company's logo inside the app (U2, design §4.23.1).
 *
 * The still counterpart of the login's `CompanyBrand`: same decision (`brandMode`),
 * no animation. A logo that will not load falls back to the name, so the top of the
 * sidebar is never an empty box.
 *
 * `name: null` means no company (the platform console), which keeps the YardFlow
 * marks exactly as they were before branding existed.
 */

import { useState } from 'react';

import { brandMode } from '../features/auth/branding';
import { LogoMark, Wordmark } from './Logo';

export function CompanyMark({
  name,
  logoUrl,
  size,
}: {
  name: string | null;
  logoUrl: string | null;
  /** `sidebar`: about 40px tall, name under it. `header`: about 32px, alone on a phone. */
  size: 'sidebar' | 'header';
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const mode = brandMode(name, logoUrl, failedUrl === logoUrl);

  if (mode === 'platform') {
    return size === 'sidebar' ? (
      <>
        <Wordmark className="h-5 w-auto text-slate-900" />
        <p className="truncate text-sm text-slate-500">Platform administration</p>
      </>
    ) : (
      <LogoMark className="h-6 w-auto shrink-0 text-slate-900" labelled={false} />
    );
  }

  if (mode === 'name') {
    // No logo: the name stands in, in the same place (U2). On a phone the header
    // carries nothing else, so the name is the mark.
    return (
      <p className="truncate text-base font-semibold text-slate-900">{name}</p>
    );
  }

  const img = (
    <img
      src={logoUrl ?? undefined}
      alt={name ?? ''}
      onError={() => setFailedUrl(logoUrl)}
      className={
        size === 'sidebar'
          ? 'h-10 w-auto max-w-full object-contain object-left'
          : 'h-8 w-auto max-w-[160px] shrink-0 object-contain'
      }
    />
  );

  return size === 'sidebar' ? (
    <>
      {img}
      <p className="mt-1 truncate text-sm text-slate-500">{name}</p>
    </>
  ) : (
    img
  );
}
